import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTenant } from "@/components/AppShell";
import { cx } from "@/components/ui";
import { api } from "@/lib/api";

import { t } from "@/lib/i18n";
/** Versandarten aller aktiven Lager des Kunden (aus FFN), ohne Dopplungen, alphabetisch */
export function useLagerShippingMethods() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 5 * 60_000, retry: false });
  const methods = useMemo(() => {
    const out = new Map<string, { id: string; name: string; carrier: string | null }>();
    for (const w of q.data?.warehouses ?? []) if (w.active || w.primary) for (const m of w.shippingMethods) out.set(m.id, { id: m.id, name: m.name, carrier: m.carrier });
    return [...out.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [q.data]);
  return { methods, isLoading: q.isLoading, isError: q.isError };
}

/** Auswahl einer Lager-Versandart statt freier Eingabe einer ID */
export function ShippingMethodSelect({
  value,
  onChange,
  emptyLabel = "Versandart wählen …",
  className,
  disabled,
  ariaLabel = "Versandart",
}: {
  value: string | null | undefined;
  onChange: (id: string | undefined) => void;
  /** Text der leeren Auswahl (z. B. „keine – Lager-Standard“) */
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const { methods, isLoading, isError } = useLagerShippingMethods();
  const v = value ?? "";
  return (
    <select
      value={v}
      onChange={(e) => onChange(e.target.value || undefined)}
      disabled={disabled || isLoading}
      aria-label={ariaLabel}
      title={isError ? t("Versandarten des Lagers konnten nicht geladen werden") : undefined}
      className={cx("h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]", className)}
    >
      <option value="">{isLoading ? t("Lade Versandarten …") : emptyLabel}</option>
      {methods.map((m) => (
        <option key={m.id} value={m.id}>{m.name}{m.carrier ? ` · ${m.carrier}` : ""}</option>
      ))}
      {/* gespeicherter Wert, den das Lager nicht (mehr) kennt – sichtbar statt still verloren */}
      {v && !isLoading && !methods.some((m) => m.id === v) && <option value={v}>{v} {t("(nicht im Lager)")}</option>}
    </select>
  );
}
